import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatMenu,
  MatMenuContent,
  MatMenuItem,
  MatMenuTrigger
} from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { AccountPickerComponent } from '@app/layout/components/account-picker/account-picker.component';
import { ClientPickerComponent } from '@app/layout/components/client-picker/client-picker.component';
import { ProjectPickerComponent } from '@app/layout/components/project-picker/project-picker.component';
import { ClientAccess } from '@core/models/client-access';
import { Module } from '@core/models/module';
import { Project } from '@core/models/project';
import { ACCESS_STORE } from '@core/stores/access.store';
import { APP_STORE } from '@core/stores/app.store';
import { USER_STORE } from '@core/stores/user.store';
import { AccountSignatureComponent } from '@shared/components/account-signature/account-signature.component';
import { Account } from '@shared/models/account';
import { AccountType } from '@shared/models/account-type';
import { FindByPropertyPipe } from '@shared/pipes/find-by-property.pipe';
import { RouterService } from '@shared/services/router.service';
import { ENVIRONMENT } from '@env/environment';
import { AuthService } from '@auth/services/auth/auth.service';
import { FilesService } from '@file-upload/services/files.service';
import { NotificationsBellComponent } from '@notifications/containers/notifications-bell/notifications-bell.component';
import { ToastNotificationsService } from '@notifications/services/toast-notifications.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouterLink,

    MatButton,
    MatIcon,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatToolbar,

    AccountPickerComponent,
    AccountSignatureComponent,
    ClientPickerComponent,
    FindByPropertyPipe,
    NotificationsBellComponent,
    ProjectPickerComponent
  ]
})
export class NavbarComponent {
  readonly #accessStore = inject(ACCESS_STORE);
  readonly #appStore = inject(APP_STORE);
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #routerService = inject(RouterService);
  readonly #filesService = inject(FilesService);
  readonly #userStore = inject(USER_STORE);
  readonly #toastNotificationsService = inject(ToastNotificationsService);
  readonly #destroyRef = inject(DestroyRef);

  readonly user = this.#userStore.data;
  readonly selectedAccount = this.#appStore.selectedAccount;
  readonly modules = this.#accessStore.modules.entities;
  readonly projects = this.#accessStore.projects.entities;
  readonly clients = this.#accessStore.clients.entities;
  readonly selectedProjectId = this.#appStore.selectedProjectId;
  readonly selectedClientId = this.#appStore.selectedClientId;
  readonly isSystemAdmin = computed(
    () => this.selectedAccount()?.accountType === AccountType.SystemAdmin
  );
  readonly pickersDisabled = computed(
    () =>
      this.#appStore.clientSetup.loading() ||
      this.#appStore.projectSetup.loading()
  );
  readonly displayAccountPicker = computed(() => {
    const selectedAccountId = this.#appStore.selectedAccountId();
    return (
      !this.isSystemAdmin() && selectedAccountId && this.user().accounts?.length
    );
  });
  readonly displayClientPicker = computed(() => {
    const selectedClientId = this.#appStore.selectedClientId();
    return (
      this.isSystemAdmin() &&
      selectedClientId &&
      this.#accessStore.clients.loaded() &&
      this.#accessStore.clients.entities()?.length
    );
  });
  readonly displayProjectPicker = computed(() => {
    const selectedProjectId = this.#appStore.selectedProjectId();
    return (
      selectedProjectId &&
      this.#accessStore.projects.loaded() &&
      this.#accessStore.projects.entities()?.length
    );
  });
  readonly modulesWithAccess = computed(
    () => this.modules()?.filter(x => this.hasModuleAccess(x)) ?? []
  );
  readonly modulesWithoutAccess = computed(
    () => this.modules()?.filter(x => !this.hasModuleAccess(x)) ?? []
  );

  readonly CoreModule = ENVIRONMENT.coreModule as Module;

  selectAccount(account: Account): void {
    this.#appStore.selectAccount(account.id, account.client?.id || '');

    void this.#router.navigate(['/client-setup'], {
      queryParams: {
        clientId: account.client?.id,
        redirectTo: this.#routerService.currentParentRoute
      }
    });
  }

  selectClient(selectedClient: ClientAccess): void {
    this.#appStore.selectClient(selectedClient.id);

    void this.#router.navigate(['/client-setup'], {
      queryParams: {
        clientId: selectedClient.id,
        redirectTo: this.#routerService.currentParentRoute
      }
    });
  }

  selectProject(selectedProject: Project): void {
    this.#appStore.selectProject(selectedProject.id);

    const selectedClientId = this.#appStore.selectedClientId();

    void this.#router.navigate(['/project-setup'], {
      queryParams: {
        clientId: selectedClientId,
        projectId: selectedProject.id,
        redirectTo: this.#routerService.currentParentRoute
      }
    });
  }

  downloadUserManual(): void {
    this.#toastNotificationsService.info(
      'Q Quick Start Guide started downloading'
    );

    this.#filesService
      .downloadFromUrl(ENVIRONMENT.assets.userManual, 'Q Quick Start Guide.pdf')
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        catchError(() => {
          this.#toastNotificationsService.error('User manual download failed');

          return of(null);
        })
      )
      .subscribe();
  }

  onUserProfileMenuTriggered(): void {
    setTimeout(() => {
      const selectedClient = document.getElementsByClassName(
        'selectedClient'
      )[0] as HTMLElement;

      const selectedAccount = document.getElementsByClassName(
        'selectedAccount'
      )[0] as HTMLElement;

      (selectedClient ?? selectedAccount)?.scrollIntoView({ block: 'start' });
    });
  }

  navigateToModule(module: Module): void {
    const clientId = this.#appStore.selectedClientId();
    const query = clientId ? `?clientId=${clientId}` : '';
    let url = `${module.locationUrl}${query}`;

    if (
      module.name !== ENVIRONMENT.coreModule.name &&
      !this.hasModuleAccess(module)
    ) {
      url = module.landingPageUrl;
    }

    if (url !== ENVIRONMENT.appRoot) {
      window.open(url);
    } else {
      window.location.href = url;
    }
  }

  navigateToMyAccount(account: Account): void {
    let location = `${this.CoreModule.locationUrl}my-profile`;

    if (account.accountType !== AccountType.SystemAdmin) {
      location += `?clientId=${account.client?.id}`;
    }

    window.open(location);
  }

  async signOut(): Promise<void> {
    this.#authService.signOut();
  }

  //#region Private methods

  private hasModuleAccess(module: Module): boolean {
    const account = this.selectedAccount();
    return account
      ? account.accountType === AccountType.SystemAdmin ||
          !!account.client?.modules?.includes(module.id || '')
      : false;
  }

  //#endregion
}
